import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, AccountSelect } from "@/components/FormBits";
import LinkExternalForm from "@/components/transfers/LinkExternalForm";
import TxTable from "@/components/TxTable";

export default function ExternalPanel({ accounts, onDone }) {
  const [ext, setExt] = useState([]);
  const [f, setF] = useState({ account_id: "", external_account_id: "", amount: "", direction: "out", memo: "" });
  const [k, setK] = useState(0);
  const load = () => api.get("/external-accounts").then((r) => setExt(r.data));
  useEffect(() => { load(); }, []);
  const set = (key) => (v) => setF((p) => ({ ...p, [key]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.account_id || !f.external_account_id) return toast.error("Choose both accounts");
    try {
      await api.post("/transfers/external", { ...f, amount: parseFloat(f.amount) });
      toast.success(f.direction === "out"
        ? `${money(parseFloat(f.amount))} transfer submitted — pending verification`
        : `${money(parseFloat(f.amount))} received into your account`);
      setF((p) => ({ ...p, amount: "", memo: "" })); setK(k + 1); onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => { await api.delete(`/external-accounts/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="tile" data-testid="external-transfer-form">
        <div className="p-7 grid gap-5">
          <h3 className="font-display font-semibold text-[var(--navy)]">Transfer with a linked account</h3>
          <Field label="Direction">
            <Select value={f.direction} onValueChange={set("direction")}>
              <SelectTrigger data-testid="external-direction-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="out">Send to external account</SelectItem><SelectItem value="in">Receive from external account</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Maderian account"><AccountSelect accounts={accounts} value={f.account_id} onChange={set("account_id")} testid="external-account-select" /></Field>
          <Field label="Linked external account">
            <Select value={f.external_account_id} onValueChange={set("external_account_id")}>
              <SelectTrigger data-testid="external-linked-select"><SelectValue placeholder={ext.length ? "Select linked account" : "Link an account first"} /></SelectTrigger>
              <SelectContent>{ext.map((x) => <SelectItem key={x.id} value={x.id}>{x.nickname} — {x.bank_name} ...{x.account_last4}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Amount"><Input type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => set("amount")(e.target.value)} required data-testid="external-amount-input" /></Field>
          <Field label="Memo (optional)"><Input value={f.memo} onChange={(e) => set("memo")(e.target.value)} data-testid="external-memo-input" /></Field>
          <Button className="rounded-full bg-[var(--blue)] hover:bg-[var(--navy)] h-11" data-testid="external-submit-button">Submit transfer</Button>
        </div>
      </form>
      <div className="grid gap-6 content-start">
        <LinkExternalForm onLinked={load} />
        <div className="tile" data-testid="linked-accounts-list">
          <div className="p-6">
            <h3 className="font-display font-semibold text-[var(--navy)] mb-3">Linked accounts</h3>
            {ext.length === 0 && <p className="text-sm text-slate-500">No linked accounts yet.</p>}
            {ext.map((x) => (
              <div key={x.id} className="flex justify-between items-center py-2 border-b last:border-0" data-testid={`linked-account-${x.id}`}>
                <div><div className="font-semibold text-sm">{x.nickname}</div><div className="text-xs text-slate-500">{x.bank_name} ...{x.account_last4}</div></div>
                <button onClick={() => remove(x.id)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`remove-linked-${x.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-2">
        <h3 className="text-lg font-bold text-[var(--navy)] mb-4">External transfer activity</h3>
        <TxTable refreshKey={k} fixedType="external" />
      </div>
    </div>
  );
}
